import React, { useState, useEffect, useCallback, useRef } from 'react';
import eventBus from '../core/EventBus';

const STEPS = [
  {
    id: 'welcome',
    title: 'Willkommen!',
    text: 'Kurze Einführung in nur 10 Sekunden — folge diesen Schritten für ein perfektes Design:',
    target: null,
    icon: '👋',
  },
  {
    id: 'bgcolor',
    title: '1. Hintergrundfarbe',
    text: 'Wähle eine Hintergrundfarbe für dein Design.',
    target: '[data-guide-id="tool-bgcolor"]',
    targetLabel: 'Hintergrundfarbe',
    icon: '🎨',
  },
  {
    id: 'bgimage',
    title: '2. Hintergrundbild',
    text: 'Lade ein eigenes Hintergrundbild hoch oder suche bei Pixabay nach lizenzfreien Bildern.',
    target: '[data-guide-id="tool-bgimage"]',
    targetLabel: 'Hintergrundbild',
    icon: '🖼',
  },
  {
    id: 'image',
    title: '3. Bilder platzieren',
    text: 'Füge eigene Bilder oder lizenzfreie Bilder von Pixabay ein.',
    target: '[data-guide-id="tool-image"]',
    targetLabel: 'Bild einfügen',
    icon: '🖼',
  },
  {
    id: 'cliparts',
    title: '4. Cliparts hinzufügen',
    text: 'Wähle aus unserer Clipart-Bibliothek passende Grafiken.',
    target: '[data-guide-id="tool-cliparts"]',
    targetLabel: 'Cliparts',
    icon: '⭐',
  },
  {
    id: 'text',
    title: '5. Texte hinzufügen',
    text: 'Setze Überschriften und Texte über deine Bilder.',
    target: '[data-guide-id="tool-text"]',
    targetLabel: 'Text',
    icon: '✏',
  },
  {
    id: 'collage',
    title: '6. Collagen & Layouts',
    text: 'Erstelle Fotocollagen mit vorgefertigten Layouts oder Zahlencollage für Geburtstage.',
    target: '[data-guide-id="tool-collage"]',
    targetLabel: 'Collagen Layouts',
    icon: '🖼',
  },
  {
    id: 'shapes',
    title: '7. Formen & Deko',
    text: 'Ergänze dein Design mit Formen, QR-Codes oder Rahmen.',
    target: '[data-guide-id="tool-shapes"]',
    targetLabel: 'Formen',
    icon: '◆',
  },
  {
    id: 'layers',
    title: '8. Ebenen prüfen',
    text: 'Ordne die Reihenfolge deiner Elemente per Drag & Drop.',
    target: '[data-guide-id="tool-layers"]',
    targetLabel: 'Ebenen',
    icon: '📑',
  },
];

function OnboardingGuide({ onDone }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [visible, setVisible] = useState(true);
  const [targetRect, setTargetRect] = useState(null);
  const panelRef = useRef(null);

  const step = STEPS[currentStep];

  // Measure target element position
  const measureTarget = useCallback(() => {
    if (!step?.target) {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(step.target);
    if (el) {
      const r = el.getBoundingClientRect();
      setTargetRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    } else {
      setTargetRect(null);
    }
  }, [step]);

  useEffect(() => {
    measureTarget();
    window.addEventListener('resize', measureTarget);
    return () => window.removeEventListener('resize', measureTarget);
  }, [measureTarget]);

  const handleNext = useCallback(() => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setVisible(false);
      setTimeout(() => onDone && onDone(), 300);
    }
  }, [currentStep, onDone]);

  const handleSkip = useCallback(() => {
    setVisible(false);
    setTimeout(() => onDone && onDone(), 300);
  }, [onDone]);

  const handleBack = useCallback(() => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  }, [currentStep]);

  // Close on stage click
  useEffect(() => {
    const unsub = eventBus.on('toolbar:close-dialogs', () => {});
    return unsub;
  }, []);

  if (!visible) return null;

  const isFirst = currentStep === 0;
  const isLast = currentStep === STEPS.length - 1;
  const progress = ((currentStep + 1) / STEPS.length) * 100;

  // Position panel next to target or centered
  let panelStyle = {};
  if (targetRect) {
    panelStyle = {
      position: 'fixed',
      top: Math.max(80, targetRect.top - 20),
      left: targetRect.left + targetRect.width + 16,
      zIndex: 700,
    };
    // If panel would overflow right, move it
    if (targetRect.left + targetRect.width + 330 > window.innerWidth) {
      panelStyle.left = 'auto';
      panelStyle.right = 20;
    }
  } else {
    panelStyle = {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 700,
    };
  }

  return (
    <>
      {/* Semi-transparent backdrop */}
      <div
        className="fixed inset-0 z-[650] transition-opacity duration-300"
        style={{ background: 'rgba(0,0,0,0.3)', opacity: visible ? 1 : 0 }}
        onClick={handleSkip}
      />

      {/* Highlight ring on target */}
      {targetRect && (
        <>
          {/* Bright cutout around target */}
          <div
            className="fixed z-[660] rounded-lg spotlight-ring"
            style={{
              top: targetRect.top - 4,
              left: targetRect.left - 4,
              width: targetRect.width + 8,
              height: targetRect.height + 8,
              background: 'rgba(255,255,255,0.95)',
              boxShadow: '0 0 0 3px rgba(59,130,246,0.6), 0 0 20px rgba(59,130,246,0.3)',
            }}
          />
          {/* Connecting line from target to panel */}
          <svg className="fixed inset-0 z-[665] pointer-events-none" style={{ width: '100%', height: '100%' }}>
            <line
              x1={targetRect.left + targetRect.width + 4}
              y1={targetRect.top + targetRect.height / 2}
              x2={targetRect.left + targetRect.width + 14}
              y2={targetRect.top + targetRect.height / 2 - 10}
              stroke="rgba(59,130,246,0.4)"
              strokeWidth="2"
              strokeDasharray="4 3"
            />
          </svg>
        </>
      )}

      {/* Guide panel */}
      <div
        ref={panelRef}
        className="animate-assistant-panel-in"
        style={panelStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-[300px] bg-white rounded-2xl shadow-2xl border border-gray-200/80 overflow-hidden">
          {/* Progress bar */}
          <div className="h-1 bg-gray-100">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-500 rounded-r"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Header */}
          <div className="px-5 pt-4 pb-2 flex items-start gap-3">
            <span className="text-2xl leading-none mt-0.5">{step.icon}</span>
            <div className="flex-1">
              <p className="text-sm font-bold text-gray-800">{step.title}</p>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">{step.text}</p>
            </div>
            <button
              onClick={handleSkip}
              className="text-gray-400 hover:text-gray-600 transition-colors shrink-0"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>

          {/* Step indicator */}
          <div className="px-5 py-2 flex items-center gap-1">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === currentStep ? 'bg-blue-500 w-6' : i < currentStep ? 'bg-blue-200 w-3' : 'bg-gray-200 w-3'
                }`}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="px-5 pb-4 pt-1 flex items-center justify-between">
            <div>
              {!isFirst && (
                <button
                  onClick={handleBack}
                  className="text-xs text-gray-500 hover:text-gray-700 transition-colors px-2 py-1"
                >
                  Zurück
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSkip}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors px-2 py-1"
              >
                Überspringen
              </button>
              <button
                onClick={handleNext}
                className="text-xs font-semibold bg-blue-500 text-white px-4 py-1.5 rounded-lg hover:bg-blue-600 transition-colors"
              >
                {isLast ? 'Los gehts!' : 'Weiter'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default OnboardingGuide;
